<template>
  <article class="blog-post-card" :class="{ 'grid-mode': viewMode === 'grid', 'list-mode': viewMode === 'list' }" data-aos="fade-up">
    <!-- Grid View -->
    <template v-if="viewMode === 'grid'">
      <div class="post-image-wrapper">
        <img 
          :src="getPostImageUrl(post.featured_image)"
          :alt="post.title"
          class="post-image"
          loading="lazy"
          decoding="async"
          @error="handlePostImageError"
        >
        <div class="post-category-badge">
          {{ post.category_name }}
        </div>
        <div class="post-actions">
          <button 
            v-if="showQuickView"
            class="action-btn quick-view-btn"
            @click="$emit('quick-view', post)"
            title="Γρήγορη προβολή"
          >
            <i class="bi bi-eye"></i>
          </button>
          <button 
            v-if="showWishlist"
            class="action-btn wishlist-btn post-wishlist-btn"
            @click="$emit('toggle-wishlist', post)"
            :class="{ 'active-wishlist': isInWishlist }"
            :title="isInWishlist ? 'Αφαίρεση από αγαπημένα' : 'Προσθήκη στα αγαπημένα'"
          >
            <i class="bi" :class="isInWishlist ? 'bi-heart-fill' : 'bi-heart'"></i>
          </button>
        </div>
      </div>
      <div class="post-content">
        <h3 class="post-title">
          <router-link :to="`/blog/${post.slug}`" class="post-link">
            {{ post.title }}
          </router-link>
        </h3>
        <p v-if="showExcerpt" class="post-excerpt" v-html="post.excerpt"></p>
        <div class="post-meta">
          <span class="post-views">
            <i class="bi bi-eye me-1"></i>
            {{ post.views || 0 }}
          </span>
          <span class="post-reading-time">
            <i class="bi bi-clock me-1"></i>
            {{ post.reading_time || 5 }}
          </span>
        </div>
        <router-link 
          v-if="showReadMore"
          :to="`/blog/${post.slug}`" 
          class="btn btn-sm mt-3 read-more-btn"
        >
          {{ readMoreText }}
          <i class="bi bi-arrow-right ms-1"></i>
        </router-link>
      </div>
    </template>

    <!-- List View -->
    <template v-else-if="viewMode === 'list'">
      <div class="row g-0">
        <div class="col-md-4">
          <div class="post-image-wrapper">
            <img 
              :src="getPostImageUrl(post.featured_image)"
              :alt="post.title"
              class="post-image"
              loading="lazy"
              decoding="async"
              @error="handlePostImageError"
            >
            <div class="post-category-badge">
              {{ post.category_name }}
            </div>
            <div class="post-actions">
              <button 
                v-if="showQuickView"
                class="action-btn quick-view-btn"
                @click="$emit('quick-view', post)"
                title="Γρήγορη προβολή"
              >
                <i class="bi bi-eye"></i>
              </button>
              <button 
                v-if="showWishlist"
                class="action-btn wishlist-btn post-wishlist-btn"
                @click="$emit('toggle-wishlist', post)"
                :class="{ 'active-wishlist': isInWishlist }"
                :title="isInWishlist ? 'Αφαίρεση από αγαπημένα' : 'Προσθήκη στα αγαπημένα'"
              >
                <i class="bi" :class="isInWishlist ? 'bi-heart-fill' : 'bi-heart'"></i>
              </button>
            </div>
          </div>
        </div>
        <div class="col-md-8">
          <div class="post-content">
            <h3 class="post-title">
              <router-link :to="`/blog/${post.slug}`" class="post-link">
                {{ post.title }}
              </router-link>
            </h3>
            <p v-if="showExcerpt" class="post-excerpt" v-html="post.excerpt"></p>
            <div class="post-meta">
              <span class="post-views">
                <i class="bi bi-eye me-1"></i>
                {{ post.views || 0 }}
              </span>
              <span class="post-reading-time">
                <i class="bi bi-clock me-1"></i>
                {{ post.reading_time || 5 }}
              </span>
            </div>
            <router-link 
              v-if="showReadMore"
              :to="`/blog/${post.slug}`" 
              class="btn btn-sm mt-3 read-more-btn"
            >
              {{ readMoreText }}
              <i class="bi bi-arrow-right ms-1"></i>
            </router-link>
          </div>
        </div>
      </div>
    </template>

    <!-- Compact View (for widgets/sidebars) -->
    <template v-else-if="viewMode === 'compact'">
      <div class="recent-post-item">
        <div class="recent-post-image">
          <img 
            :src="getPostImageUrl(post.featured_image)"
            :alt="post.title"
            loading="lazy"
            decoding="async"
            @error="handlePostImageError"
          >
        </div>
        <div class="recent-post-content">
          <h6>
            <router-link :to="`/blog/${post.slug}`">{{ post.title }}</router-link>
          </h6>
          <div class="post-meta-compact">
            <small class="text-muted">
              <i class="bi bi-eye me-1"></i>{{ post.views || 0 }}
            </small>
            <small class="text-muted ms-2">
              <i class="bi bi-clock me-1"></i>{{ post.reading_time || 5 }}
            </small>
          </div>
        </div>
      </div>
    </template>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { IMAGE_BASE_URL } from '@/utils/config'

// Props - Using generic interface to accept any Post type
interface Props {
  post: any  // Accept any post object to avoid type conflicts
  viewMode?: 'grid' | 'list' | 'compact'
  isInWishlist?: boolean
  showQuickView?: boolean
  showWishlist?: boolean
  showExcerpt?: boolean
  showReadMore?: boolean
  readMoreText?: string
}

const props = withDefaults(defineProps<Props>(), {
  viewMode: 'grid',
  isInWishlist: false,
  showQuickView: true,
  showWishlist: true,
  showExcerpt: true,
  showReadMore: true,
  readMoreText: 'Περισσότερα'
})

// Emits
defineEmits<{
  'quick-view': [post: any]
  'toggle-wishlist': [post: any]
}>()

// Methods
const getPostImageUrl = (filename: string): string => {
  if (!filename) return `${IMAGE_BASE_URL}?type=post&file=uploads/imgs/images/placeholder.jpg`
  return `${IMAGE_BASE_URL}?type=post&file=${filename}`
}

const handlePostImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  img.src = `${IMAGE_BASE_URL}?type=post&file=uploads/imgs/images/placeholder.jpg`
}
</script>

<style scoped>
/* Grid Mode */
.blog-post-card.grid-mode {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.blog-post-card.grid-mode:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  border-color: rgba(220, 53, 69, 0.2);
}

/* List Mode */
.blog-post-card.list-mode {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 2rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.blog-post-card.list-mode:hover {
  transform: translateX(8px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  border-color: rgba(220, 53, 69, 0.2);
}

/* Compact Mode (for widgets) */
.recent-post-item {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
}

.recent-post-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.recent-post-image {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.recent-post-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.recent-post-content {
  flex: 1;
}

.recent-post-content h6 {
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
  line-height: 1.4;
}

.recent-post-content h6 a {
  color: #333;
  text-decoration: none;
  transition: color 0.3s;
}

.recent-post-content h6 a:hover {
  color: #dc3545;
}

.post-meta-compact {
  display: flex;
  gap: 0.5rem;
  font-size: 0.75rem;
}

/* Common Styles */
.post-image-wrapper {
  position: relative;
  overflow: hidden;
}

.grid-mode .post-image-wrapper {
  aspect-ratio: 4/3;
}

.list-mode .post-image-wrapper {
  height: 100%;
  min-height: 250px;
  aspect-ratio: 4/3;
}

.post-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.blog-post-card:hover .post-image {
  transform: scale(1.05);
}

.post-category-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  padding: 0.25rem 0.6rem;
  border-radius: 20px;
  font-size: 0.65rem;
  font-weight: 600;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
}

.post-actions {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 2;
}

.blog-post-card:hover .post-actions {
  opacity: 1;
}

.action-btn {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 3px 10px rgba(220, 53, 69, 0.4);
}

.action-btn:hover {
  background: linear-gradient(135deg, #c82333 0%, #a71d2a 100%);
  transform: scale(1.15);
  box-shadow: 0 5px 15px rgba(220, 53, 69, 0.6);
}

.action-btn.active-wishlist {
  background: linear-gradient(135deg, #ff4757 0%, #ff3838 100%);
  box-shadow: 0 5px 15px rgba(255, 71, 87, 0.5);
}

.post-content {
  padding: 1.25rem;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.list-mode .post-content {
  padding: 1.5rem;
}

.post-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  line-height: 1.4;
}

.list-mode .post-title {
  font-size: 1.5rem;
  margin-bottom: 1rem;
}

.post-link {
  color: #333;
  text-decoration: none;
  transition: color 0.3s;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.post-link:hover {
  color: #dc3545;
}

.post-excerpt {
  color: #666;
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
}

.list-mode .post-excerpt {
  font-size: 1rem;
  -webkit-line-clamp: 4;
  line-clamp: 4;
}

.post-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.85rem;
  color: #666;
  margin-top: auto;
}

.post-views, .post-reading-time {
  display: flex;
  align-items: center;
}

.btn-primary {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #c82333 0%, #a71d2a 100%);
  transform: translateX(3px);
}

.read-more-btn {
  background-color: #0a2d5e;
  color: #ffffff !important;
  border: none;
  font-weight: 500;
  transition: all 0.3s ease;
}

.read-more-btn:hover {
  background-color: #0c3a7a;
  color: #ffffff !important;
  transform: translateX(3px);
}

/* Mobile Responsiveness */
@media (max-width: 576px) {
  .post-actions {
    opacity: 1;
  }
  
  .action-btn {
    width: 32px;
    height: 32px;
    font-size: 0.9rem;
  }
  
  .post-title {
    font-size: 1rem;
  }
  
  .post-excerpt {
    font-size: 0.85rem;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }
  
  .list-mode .post-image-wrapper {
    min-height: 200px;
  }
  
  .recent-post-image {
    width: 60px;
    height: 60px;
  }
}
</style>
